import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { api, fmtDate, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";

echarts.use([LineChart, GridComponent, LegendComponent, TooltipComponent, SVGRenderer]);

/** Pulse (admin spec §19.2): week-over-week tiles and a 30-day series, all from `daily_facts`. */

interface Tile {
  key: string;
  value: number;
  previous: number;
  deltaPct: number | null;
  mode: "avg" | "sum";
}

interface PulseData {
  days: number;
  tiles: Tile[];
  series: Array<{ day: string } & Record<string, number | string | undefined>>;
  latest: { day: string; wau: number; members: number; guests: number; famMembers: number; balancedShare: number } | null;
}

const SERIES = ["dau", "check_ins", "prayers", "messages", "sessions_completed"] as const;
/** Series colours = the dimension colours of the current theme (read from the tokens at mount). */
function chartColors(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const v = (name: string) => cs.getPropertyValue(name).trim();
  return { series: [v("--dim-grow"), v("--dim-pray"), v("--dim-serve"), v("--coral"), v("--dim-give")], ink: v("--ink"), muted: v("--ink-muted"), line: v("--line") };
}

export function AdminPulse() {
  const { t, locale } = useT();
  const scope = useScope();
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ["admin", "pulse", scope.churchId, days], queryFn: () => api<PulseData>(`/admin/pulse${scopeQuery(scope, "church", { days: String(days) })}`) });
  const d = q.data;

  return (
    <>
      <h1>{t("admin.pulse.title")}</h1>
      <p className="muted">{t("admin.pulse.body")}</p>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      {d && d.series.length === 0 ? <p className="muted empty">{t("admin.pulse.empty")}</p> : null}

      {d?.latest ? (
        <>
          <p className="eyebrow">{t("admin.pulse.latest", { day: fmtDate(d.latest.day, locale) })}</p>
          <div className="tiles">
            <div className="tile">
              <div className="l">{t("admin.pulse.wau")}</div>
              <div className="n">{d.latest.wau}</div>
            </div>
            <div className="tile">
              <div className="l">{t("admin.pulse.members")}</div>
              <div className="n">{d.latest.members}</div>
            </div>
            <div className="tile">
              <div className="l">{t("admin.pulse.guests")}</div>
              <div className="n">{d.latest.guests}</div>
            </div>
            <div className="tile">
              <div className="l">{t("admin.pulse.famMembers")}</div>
              <div className="n">{d.latest.famMembers}</div>
            </div>
            <div className="tile" title={t("admin.pulse.balancedHint")}>
              <div className="l">{t("admin.pulse.balanced")}</div>
              <div className="n">{d.latest.balancedShare}%</div>
            </div>
          </div>
        </>
      ) : null}

      <h2 className="section">{t("admin.pulse.wow")}</h2>
      <div className="tiles pulse">
        {(d?.tiles ?? []).map((tile) => (
          <div key={tile.key} className="tile">
            <div className="l">{t(`admin.pulse.metric.${tile.key}`)}</div>
            <div className="n">{tile.value.toLocaleString(locale)}</div>
            <div className={`delta ${tile.deltaPct == null ? "" : tile.deltaPct >= 0 ? "up" : "down"}`}>
              {tile.deltaPct == null ? "–" : `${tile.deltaPct > 0 ? "+" : ""}${tile.deltaPct}%`} <span className="muted">· {tile.previous.toLocaleString(locale)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <h2 className="section">{t("admin.pulse.chart", { days })}</h2>
        <div className="chips">
          {[14, 30, 90].map((n) => (
            <button key={n} className={`btn small ${n === days ? "" : "ghost"}`} onClick={() => setDays(n)}>
              {n}
            </button>
          ))}
        </div>
      </div>
      {d && d.series.length > 0 ? <Chart series={d.series} locale={locale} labels={Object.fromEntries(SERIES.map((k) => [k, t(`admin.pulse.series.${k}`)]))} /> : null}
    </>
  );
}

function Chart({ series, locale, labels }: { series: PulseData["series"]; locale: string; labels: Record<string, string> }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current, undefined, { renderer: "svg" });
    const c = chartColors(ref.current);
    chart.setOption({
      color: c.series,
      textStyle: { color: c.ink, fontFamily: "inherit" },
      tooltip: { trigger: "axis" },
      legend: { bottom: 0, textStyle: { fontFamily: "inherit", color: c.ink } },
      grid: { left: 40, right: 16, top: 16, bottom: 48 },
      xAxis: { type: "category", data: series.map((s) => fmtDate(s.day, locale, { day: "numeric", month: "short" })), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: c.muted } },
      yAxis: { type: "value", splitLine: { lineStyle: { color: c.line } }, axisLabel: { color: c.muted } },
      series: SERIES.map((k) => ({ name: labels[k], type: "line", smooth: true, showSymbol: false, data: series.map((s) => (typeof s[k] === "number" ? (s[k] as number) : 0)) })),
    });
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [series, locale, labels]);
  return <div ref={ref} className="chart" />;
}
